import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.jsx';
import { OnboardingProvider } from './onboarding/OnboardingProvider.jsx';
import { ImpersonationBanner } from './components/ImpersonationBanner.jsx';
import { FeatureGate } from './components/FeatureGate.jsx';
import { ProtectedRoute } from './components/ProtectedRoute.jsx';
import { LoginPage } from './pages/LoginPage.jsx';
import { RegisterPage } from './pages/RegisterPage.jsx';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.jsx';
import { ResetPasswordPage } from './pages/ResetPasswordPage.jsx';
import { DashboardPage } from './pages/DashboardPage.jsx';
import { AdminLayout } from './pages/AdminLayout.jsx';
import { AdminOverviewPage } from './pages/AdminOverviewPage.jsx';
import { AdminFormsPage } from './pages/AdminFormsPage.jsx';
import { AdminFormEditorPage } from './pages/AdminFormEditorPage.jsx';
import { PublicFormPage } from './pages/PublicFormPage.jsx';
import { MyFormsPage } from './pages/MyFormsPage.jsx';
import { MyFormFillPage } from './pages/MyFormFillPage.jsx';
import { AdminAssetsPage } from './pages/AdminAssetsPage.jsx';
import { AdminEmailsPage } from './pages/AdminEmailsPage.jsx';
import { AdminEmailTemplatesPage } from './pages/AdminEmailTemplatesPage.jsx';
import { AdminMeetingsPage } from './pages/AdminMeetingsPage.jsx';
import { AdminNotificationsPage } from './pages/AdminNotificationsPage.jsx';
import { AdminContactsPage } from './pages/AdminContactsPage.jsx';
import { AdminTasksPage } from './pages/AdminTasksPage.jsx';
import { AdminPdfDocumentsPage } from './pages/AdminPdfDocumentsPage.jsx';
import { AdminPdfDocumentEditorPage } from './pages/AdminPdfDocumentEditorPage.jsx';
import { AdminClientsPage } from './pages/AdminClientsPage.jsx';
import { MyFaqPage } from './pages/MyFaqPage.jsx';
import { AdminClientDetailPage } from './pages/AdminClientDetailPage.jsx';
import { AdminProjectsPage } from './pages/AdminProjectsPage.jsx';
import { AdminProjectTemplatesPage } from './pages/AdminProjectTemplatesPage.jsx';
import { AdminProjectTemplateEditorPage } from './pages/AdminProjectTemplateEditorPage.jsx';
import { AdminProjectDetailPage } from './pages/AdminProjectDetailPage.jsx';
import { AdminIntegrationsPage } from './pages/AdminIntegrationsPage.jsx';
import { AdminPm2Page } from './pages/AdminPm2Page.jsx';
import { AdminPm2AppsPage } from './pages/AdminPm2AppsPage.jsx';
import { AdminDockerPage } from './pages/AdminDockerPage.jsx';
import { AdminGhlLocationsPage } from './pages/AdminGhlLocationsPage.jsx';
import { AdminBillingPage } from './pages/AdminBillingPage.jsx';
import { AdminTeamPage } from './pages/AdminTeamPage.jsx';
import { AdminTeamAccessPage } from './pages/AdminTeamAccessPage.jsx';
import { AdminTimeTrackerPage } from './pages/AdminTimeTrackerPage.jsx';
import { SuperAdminLayout } from './pages/SuperAdminLayout.jsx';
import { SuperAdminOverviewPage } from './pages/SuperAdminOverviewPage.jsx';
import { SuperAdminPlansPage } from './pages/SuperAdminPlansPage.jsx';
import { SuperAdminClientsPage } from './pages/SuperAdminClientsPage.jsx';
import { SuperAdminUsersPage } from './pages/SuperAdminUsersPage.jsx';
import { MyPortalLayout } from './pages/MyPortalLayout.jsx';
import { MyDashboardPage } from './pages/MyDashboardPage.jsx';
import { MyFilesPage } from './pages/MyFilesPage.jsx';
import { MyPdfDocumentsPage } from './pages/MyPdfDocumentsPage.jsx';
import { MyAssetsPage } from './pages/MyAssetsPage.jsx';
import { MyEmailsPage } from './pages/MyEmailsPage.jsx';
import { MyMeetingsPage } from './pages/MyMeetingsPage.jsx';
import { MyToolsPage } from './pages/MyToolsPage.jsx';
import { MyTasksPage } from './pages/MyTasksPage.jsx';
import { MyProjectsPage } from './pages/MyProjectsPage.jsx';
import { MyProjectDetailPage } from './pages/MyProjectDetailPage.jsx';
import { AcceptInvitePage } from './pages/AcceptInvitePage.jsx';
import { AdminChatPage } from './pages/AdminChatPage.jsx';
import { AdminSettingsPage } from './pages/AdminSettingsPage.jsx';
import { GuestChatPage } from './pages/GuestChatPage.jsx';
import { AdminAuditLogPage } from './pages/AdminAuditLogPage.jsx';
import { ProfilePage } from './pages/ProfilePage.jsx';
import { SuperAdminOrganizationsPage } from './pages/SuperAdminOrganizationsPage.jsx';
import { ROLES } from './utils/roles.js';

function MyPortalHomeRedirect() {
  return <Navigate to="/my/dashboard" replace />;
}

export function App() {
  return (
    <AuthProvider>
      {/* useTransitions stays off: with a focused field (task composer, task dialog),
          React 19 drops the transition and the URL changes without the page. */}
      <BrowserRouter useTransitions={false}>
        <OnboardingProvider>
        <ImpersonationBanner />
        <Routes>
          <Route path="/" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/chat/guest/:organizationId" element={<GuestChatPage />} />
          <Route path="/f/:key" element={<PublicFormPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my"
            element={
              <ProtectedRoute roles={[ROLES.CLIENT]}>
                <MyPortalLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<MyPortalHomeRedirect />} />
            <Route path="dashboard" element={<MyDashboardPage />} />
            <Route path="forms" element={<FeatureGate capability="view_client_forms"><MyFormsPage /></FeatureGate>} />
            <Route path="forms/:id" element={<FeatureGate capability="view_client_forms"><MyFormFillPage /></FeatureGate>} />
            <Route path="faq" element={<FeatureGate capability="view_client_faq"><MyFaqPage /></FeatureGate>} />
            <Route path="files" element={<FeatureGate capability="view_portal_projects"><MyFilesPage /></FeatureGate>} />
            <Route path="pdf-documents" element={<FeatureGate capability="view_portal_projects"><MyPdfDocumentsPage /></FeatureGate>} />
            <Route path="assets" element={<FeatureGate capability="view_client_assets"><MyAssetsPage /></FeatureGate>} />
            <Route path="tasks" element={<FeatureGate capability="view_client_tasks"><MyTasksPage /></FeatureGate>} />
            <Route path="emails" element={<FeatureGate capability="view_client_emails"><MyEmailsPage /></FeatureGate>} />
            <Route path="meetings" element={<FeatureGate capability="view_client_meetings"><MyMeetingsPage /></FeatureGate>} />
            <Route path="tools" element={<FeatureGate capability="view_portal_projects"><MyToolsPage /></FeatureGate>} />
            <Route path="projects" element={<FeatureGate capability="view_portal_projects"><MyProjectsPage /></FeatureGate>} />
            <Route path="projects/:id" element={<FeatureGate capability="view_portal_projects"><MyProjectDetailPage /></FeatureGate>} />
            <Route path="profile" element={<ProfilePage backTo="/my/dashboard" />} />
          </Route>
          <Route
            path="/super-admin"
            element={
              <ProtectedRoute roles={[ROLES.SUPER_ADMIN]}>
                <SuperAdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<SuperAdminOverviewPage />} />
            <Route path="plans" element={<SuperAdminPlansPage />} />
            <Route path="clients" element={<SuperAdminClientsPage />} />
            <Route path="users" element={<SuperAdminUsersPage />} />
            <Route path="organizations" element={<SuperAdminOrganizationsPage />} />
            <Route path="profile" element={<ProfilePage backTo="/super-admin" />} />
          </Route>
          <Route
            path="/admin/billing"
            element={
              <ProtectedRoute roles={[ROLES.ADMIN]} allowWithoutSubscription>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminBillingPage />} />
          </Route>
          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.TEAM_MEMBER]} requireSubscription>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminOverviewPage />} />
            <Route path="clients" element={<FeatureGate capability="manage_clients"><AdminClientsPage /></FeatureGate>} />
            <Route path="forms" element={<FeatureGate capability="faq_forms"><AdminFormsPage /></FeatureGate>} />
            <Route path="forms/:id" element={<FeatureGate capability="faq_forms"><AdminFormEditorPage /></FeatureGate>} />
            <Route path="forms/:id/:tab" element={<FeatureGate capability="faq_forms"><AdminFormEditorPage /></FeatureGate>} />
            <Route path="assets" element={<FeatureGate capability="manage_assets"><AdminAssetsPage /></FeatureGate>} />
            <Route path="emails" element={<FeatureGate capability="view_mailbox"><AdminEmailsPage /></FeatureGate>} />
            <Route path="meetings" element={<FeatureGate capability="view_mailbox"><AdminMeetingsPage /></FeatureGate>} />
            <Route path="notifications" element={<AdminNotificationsPage />} />
            <Route path="contacts" element={<FeatureGate capability="view_contacts"><AdminContactsPage /></FeatureGate>} />
            <Route path="tasks" element={<FeatureGate capability="manage_tasks"><AdminTasksPage /></FeatureGate>} />
            <Route path="pdf-documents" element={<FeatureGate capability="manage_pdf"><AdminPdfDocumentsPage /></FeatureGate>} />
            <Route path="pdf-documents/:id" element={<FeatureGate capability="manage_pdf"><AdminPdfDocumentEditorPage /></FeatureGate>} />
            <Route path="clients/:id" element={<FeatureGate capability="manage_clients"><AdminClientDetailPage /></FeatureGate>} />
            <Route path="projects" element={<FeatureGate capability="manage_projects"><AdminProjectsPage /></FeatureGate>} />
            <Route path="project-templates" element={<FeatureGate capability="manage_projects"><AdminProjectTemplatesPage /></FeatureGate>} />
            <Route path="project-templates/:templateId" element={<FeatureGate capability="manage_projects"><AdminProjectTemplateEditorPage /></FeatureGate>} />
            <Route path="projects/:id" element={<FeatureGate capability="manage_projects"><AdminProjectDetailPage /></FeatureGate>} />
            <Route path="time-tracker" element={<FeatureGate capability="time_tracker"><AdminTimeTrackerPage /></FeatureGate>} />
            <Route path="chat" element={<FeatureGate capability="live_chat"><AdminChatPage /></FeatureGate>} />
            <Route path="settings" element={<FeatureGate capability="manage_organization"><AdminSettingsPage /></FeatureGate>} />
            <Route path="email-templates" element={<FeatureGate capability="manage_email_templates"><AdminEmailTemplatesPage /></FeatureGate>} />
            <Route path="audit-logs" element={<FeatureGate capability="view_audit_logs"><AdminAuditLogPage /></FeatureGate>} />
            <Route path="profile" element={<ProfilePage backTo="/admin" />} />
            <Route path="integrations" element={<FeatureGate capability="manage_integrations"><AdminIntegrationsPage /></FeatureGate>} />
            <Route path="pm2" element={<FeatureGate capability="pm2"><AdminPm2Page /></FeatureGate>} />
            <Route path="pm2/apps" element={<FeatureGate capability="pm2"><AdminPm2AppsPage /></FeatureGate>} />
            <Route path="docker" element={<FeatureGate capability="docker"><AdminDockerPage /></FeatureGate>} />
            <Route path="ghl-locations" element={<FeatureGate capability="manage_ghl"><AdminGhlLocationsPage /></FeatureGate>} />
            <Route path="team" element={<FeatureGate capability="manage_team"><AdminTeamPage /></FeatureGate>} />
            <Route
              path="team/access"
              element={
                <ProtectedRoute roles={[ROLES.ADMIN]} requireSubscription>
                  <AdminTeamAccessPage />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </OnboardingProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}
